// Profile — web twin of the mobile profile tab: avatar (click to upload),
// account details, password-reset email, language & appearance preferences,
// log out, version footer.

import type { ReactElement } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useA11y, type TextSize } from '../lib/a11y';
import { ApiError, avatarUrl, requestPasswordReset, updateMyProfile, uploadMyPicture } from '../lib/api';
import { logout } from '../lib/auth';
import { config } from '../lib/config';
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES, setLanguage } from '../lib/i18n';
import { useTheme, type ThemePreference } from '../lib/theme';
import { useUser } from '../lib/user';
import Icon from '../components/Icon';
import { Spinner, StateCard, Switch, toast } from '../components/ui';

// Downscale to keep the base64 payload sane (mobile uploads camera-quality
// JPEGs; the avatar is served small anyway).
async function fileToJpegBase64(file: File, maxSide = 512): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.88).split(',')[1];
  } finally {
    URL.revokeObjectURL(url);
  }
}

// timezoneOptions returns the full IANA zone list when the runtime supports
// it, otherwise a fallback of common zones — same approach as the mobile
// profile tab.
function timezoneOptions(): string[] {
  let zones: string[] = [];
  try {
    zones =
      (Intl as { supportedValuesOf?: (key: 'timeZone') => string[] }).supportedValuesOf?.(
        'timeZone',
      ) ?? [];
  } catch {
    zones = [];
  }
  return zones.length ? zones : TIMEZONE_FALLBACK;
}

const TIMEZONE_FALLBACK = [
  'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Oslo',
  'Europe/Stockholm', 'Europe/Helsinki', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Moscow', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Toronto', 'America/Sao_Paulo',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Dubai',
  'Asia/Kolkata', 'Australia/Sydney', 'Pacific/Auckland',
];

// "Europe/Oslo · GMT+02:00" — the offset hint makes the long native select
// scannable. Falls back to the bare zone id if the runtime can't compute it.
function timezoneOptionLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    return offset ? `${tz.replace(/_/g, ' ')} · ${offset}` : tz;
  } catch {
    return tz;
  }
}

export default function ProfilePage(): ReactElement {
  const { t, i18n } = useTranslation();
  const { me, loading, error, reload } = useUser();
  const { preference, setPreference } = useTheme();
  const { settings: a11y, update: updateA11y } = useA11y();

  const fileInput = useRef<HTMLInputElement>(null);
  const [avatarNonce, setAvatarNonce] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  // Local override so the select updates optimistically without a full /me
  // reload (which would flash the page spinner); reverted if the save fails.
  const [timezone, setTimezone] = useState<string | null>(null);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const tzOptions = useMemo(timezoneOptions, []);

  if (loading) return <Spinner />;
  if (error || !me) {
    return (
      <StateCard
        title={t('common.error')}
        body={error ?? undefined}
        action={
          <button className="btn" onClick={reload}>
            <Icon name="refresh" size={16} /> {t('common.retry')}
          </button>
        }
      />
    );
  }

  const onAvatarPicked = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await fileToJpegBase64(file);
      await uploadMyPicture(b64, 'image/jpeg');
      setAvatarNonce(Date.now());
      toast(t('profile.avatar.updated'));
    } catch {
      toast(t('profile.avatar.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const onPasswordReset = async () => {
    setSendingReset(true);
    try {
      await requestPasswordReset();
      toast(`${t('auth.resetSentBodyPrefix')} ${me.email}${t('auth.resetSentBodySuffix')}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        let seconds: number | undefined;
        try {
          seconds = (JSON.parse(e.message) as { seconds?: number }).seconds;
        } catch {
          /* body not JSON */
        }
        toast(
          seconds
            ? t('auth.resetThrottleSeconds', { seconds })
            : t('auth.resetThrottle'),
        );
      } else {
        toast(t('auth.resetSendError'));
      }
    } finally {
      setSendingReset(false);
    }
  };

  const onTimezonePicked = async (tz: string) => {
    const previous = timezone ?? me.timezone;
    setTimezone(tz);
    setSavingTimezone(true);
    try {
      await updateMyProfile({ timezone: tz });
      toast(t('profile.timezoneSaved'));
    } catch {
      setTimezone(previous);
      toast(t('profile.timezoneSaveFailed'));
    } finally {
      setSavingTimezone(false);
    }
  };

  const currentTimezone = timezone ?? me.timezone;
  // The account value may predate the runtime's zone list (renamed/alias
  // zones) — keep it selectable rather than showing a wrong selection.
  const tzSelectOptions =
    currentTimezone && !tzOptions.includes(currentTimezone)
      ? [currentTimezone, ...tzOptions]
      : tzOptions;

  const themeOptions: { value: ThemePreference; label: string }[] = [
    { value: 'system', label: 'System' },
    { value: 'dark', label: 'Dark' },
    { value: 'light', label: 'Light' },
  ];

  return (
    <>
      <div className="page-head">
        <div className="titles">
          <h1>{t('nav.profile')}</h1>
        </div>
      </div>

      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <button
          className="icon-btn"
          style={{ width: 'auto', height: 'auto', borderRadius: '50%' }}
          onClick={() => fileInput.current?.click()}
          data-tip={t('profile.avatar.title')}
          aria-label={t('profile.avatar.title')}
          disabled={uploading}
        >
          <img
            className="avatar avatar-lg"
            src={`${avatarUrl(me.uid, 'small')}${avatarNonce ? `?t=${avatarNonce}` : ''}`}
            alt=""
            style={{ opacity: uploading ? 0.5 : 1 }}
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
          />
        </button>
        <div>
          <h2 style={{ fontSize: '1.15rem' }}>{me.name}</h2>
          <p className="muted" style={{ fontSize: '0.9rem' }}>
            {me.email}
          </p>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void onAvatarPicked(e.target.files?.[0])}
        />
      </div>

      <div className="profile-grid" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="card-title">
            <Icon name="user" size={16} />
            {t('profile.account')}
          </div>
          <div className="profile-row">
            <span className="row-label">{t('profile.profileName')}</span>
            <span className="row-value">{me.name || '—'}</span>
          </div>
          <div className="profile-row">
            <span className="row-label">{t('profile.email')}</span>
            <span className="row-value">{me.email || '—'}</span>
          </div>
          <div className="profile-row">
            <span className="row-label">{t('profile.phone')}</span>
            <span className="row-value">{me.phone || '—'}</span>
          </div>
          <div className="profile-row">
            <span className="row-label">{t('profile.password')}</span>
            <span className="row-value">••••••••</span>
            <button className="btn-ghost btn" onClick={() => void onPasswordReset()} disabled={sendingReset}>
              {t('profile.change')}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            <Icon name="globe" size={16} />
            {t('profile.preferences')}
          </div>
          <div className="profile-row">
            <span className="row-label">{t('profile.language')}</span>
            <select
              className="select"
              value={i18n.language}
              onChange={(e) => setLanguage(e.target.value, { persist: true })}
            >
              {SUPPORTED_LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {LANGUAGE_NAMES[l]}
                </option>
              ))}
            </select>
          </div>
          <div className="profile-row">
            <span className="row-label">{t('profile.appearance')}</span>
            <select
              className="select"
              value={preference}
              onChange={(e) => setPreference(e.target.value as ThemePreference)}
            >
              {themeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="profile-row">
            <span className="row-label">{t('profile.timezone')}</span>
            <select
              className="select"
              value={currentTimezone || ''}
              disabled={savingTimezone}
              onChange={(e) => void onTimezonePicked(e.target.value)}
            >
              {!currentTimezone && <option value="">—</option>}
              {tzSelectOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {timezoneOptionLabel(tz)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Same settings as the topbar accessibility menu — this card is the
            only entry point on phones, where the topbar nav collapses. */}
        <div className="card">
          <div className="card-title">
            <Icon name="accessibility" size={16} />
            {t('a11y.title')}
          </div>
          <div className="profile-row">
            <span className="row-label">{t('a11y.textSize')}</span>
            <select
              className="select"
              value={a11y.textSize}
              onChange={(e) => updateA11y({ textSize: e.target.value as TextSize })}
            >
              <option value="normal">{t('a11y.textSizeNormal')}</option>
              <option value="large">{t('a11y.textSizeLarge')}</option>
              <option value="xlarge">{t('a11y.textSizeXlarge')}</option>
            </select>
          </div>
          <div className="profile-row">
            <span className="row-label">{t('a11y.highContrast')}</span>
            <span className="row-value muted" style={{ fontSize: '0.8rem' }}>
              {t('a11y.highContrastHelp')}
            </span>
            <Switch
              checked={a11y.highContrast}
              ariaLabel={t('a11y.highContrast')}
              onChange={(v) => updateA11y({ highContrast: v })}
            />
          </div>
          <div className="profile-row">
            <span className="row-label">{t('a11y.reduceMotion')}</span>
            <span className="row-value muted" style={{ fontSize: '0.8rem' }}>
              {t('a11y.reduceMotionHelp')}
            </span>
            <Switch
              checked={a11y.reduceMotion}
              ariaLabel={t('a11y.reduceMotion')}
              onChange={(v) => updateA11y({ reduceMotion: v })}
            />
          </div>
          <p className="muted" style={{ fontSize: '0.78rem', marginTop: 10 }}>
            {t('a11y.hint')}
          </p>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <button className="btn btn-danger" onClick={() => void logout()}>
          <Icon name="log-out" size={16} />
          {t('profile.logout')}
        </button>
      </div>

      <p className="muted" style={{ textAlign: 'center', marginTop: 20, fontSize: '0.8rem' }}>
        {t('profile.version', { version: config.appVersion })}
      </p>
    </>
  );
}
